import { Component, DestroyRef, ElementRef, EventEmitter, Output, ViewChild, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { switchMap, take } from 'rxjs/operators';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { EditorSaveStatus } from '@modules/sheets/editor-save-status';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import type { Sheet } from '@modules/sheets/types/sheet';
import { IconComponent } from '@modules/sheets/icon.component';
import { DownloadMenuComponent, type DownloadAction } from '@modules/sheets/download-menu.component';
import { EditorDownloads } from '@modules/sheets/editor-downloads';
import { EditorChrome } from '@modules/sheets/editor-chrome';
import { SheetContextMenuDirective } from '@modules/elements/directives/sheet-context-menu.directive';
import { type SheetSummaryDto, WORKBOOK_TITLE_MAX } from '@sharedlib/dtos/dto/sheets-app/sheet.dto';

/** §11.6.1: a title is 1–200 characters, the limit the details form and the API check (`WORKBOOK_TITLE_MAX`). */
export const TITLE_MAX = WORKBOOK_TITLE_MAX;

/**
 * The bar above an expanded workbook: its title, and the way back to the list.
 *
 * It exists as its own component because the app state service is provided by
 * *appSheetsAppContext, i.e. inside that directive's embedded view. The layout
 * component is the directive's HOST and therefore sits outside that injector,
 * so it cannot read the open item itself.
 */
@Component({
  selector: 'app-sheets-expanded-header',
  standalone: true,
  imports: [IconComponent, MatIconModule, DownloadMenuComponent, SheetContextMenuDirective],
  templateUrl: './sheets-expanded-header.component.html',
  styleUrls: ['./sheets-expanded-header.component.less', '../../sheets-buttons.css'],
})
export class SheetsExpandedHeaderComponent {
  @Output() closed = new EventEmitter<void>();

  private readonly stateService = inject(SheetsAppStateService);
  /** Optional, as in the editor: the unguarded spike route has no session, so it has no service. */
  private readonly sheetsService = inject(SheetsService, { optional: true });
  protected readonly saveStatus = inject(EditorSaveStatus);
  /** NFR-8: the bar's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  protected readonly TITLE_MAX = TITLE_MAX;

  /**
   * The workbook the header is for, from the item form it heads (SBUX-1036). The form keeps showing a workbook the app
   * state let go of (moved to Trash: BaseAppStateService clears the main view), so its title stays too. Unset, the
   * open workbook as the app state has it.
   */
  public readonly sheet = input<Partial<Sheet> | null | undefined>(undefined);
  private readonly selected = toSignal(this.stateService.getMainView(), { initialValue: null });
  private readonly item = computed(() => {
    const given = this.sheet();
    return given === undefined ? this.selected() : given;
  });

  /** A title being saved: shown until the save answers, and dropped if it fails. */
  private readonly pending = signal<string | null>(null);

  /**
   * SBUX-1043: a title this header saved for a workbook the SECTION does not have open.
   *
   * On the Sheets page the saved item goes into the app state and comes back through `selected()`.
   * In the fourth panel the header is given its workbook by its host, and a rename does not change
   * the host's copy — so without this the new name showed until the save answered and then reverted
   * to the old one.
   */
  private readonly renamed = signal<{ readonly id: string; readonly title: string; readonly at?: Date } | null>(null);

  /**
   * Whether a copy of the workbook knows better than our local override: it was written AFTER the
   * rename we are holding on to, so its title is a later fact, not the stale one we are covering for.
   *
   * Without this the override never lets go (SBUX-1043, q53c): rename here, have the same workbook
   * renamed somewhere else, and the header shows our name for as long as it lives. Proved with a
   * failing test before this existed.
   *
   * Both timestamps must be present to overrule the override. A copy with no `updatedAt` says
   * nothing about when it was written, and the override is the only thing keeping the new name on
   * screen, so the safe answer there is to keep it.
   */
  private static supersedes(item: { updatedAt?: Date }, mine: { at?: Date }): boolean {
    return !!item.updatedAt && !!mine.at && item.updatedAt.getTime() > mine.at.getTime();
  }

  /**
   * An item with no name gets the placeholder the rest of the app uses; NO item gets no heading at all.
   * The distinction matters because the editor below names itself whenever it is not bound to one, so a
   * fallback here would print "Untitled" twice — which is what a workbook that failed to load showed.
   */
  protected readonly title = computed(() => {
    const item = this.item() as { id?: string; title?: string; updatedAt?: Date } | null;
    if (!item) return null;
    const mine = this.renamed();
    const holds = !!mine && mine.id === item.id && !SheetsExpandedHeaderComponent.supersedes(item, mine);
    const latest = holds ? mine.title : item.title;
    return this.pending() ?? (latest || SHEETS_STRINGS.Untitled);
  });

  /** AM-48, §11.6.1: the title is renamed here, but not while the workbook is in Trash (read-only, §12). */
  protected readonly renamable = computed(() => {
    const item = this.item() as Partial<Sheet> | null;
    return !!item?.id && !item.deleted && !!this.sheetsService && this.saveStatus.current()?.state !== 'read_only';
  });

  protected readonly editing = signal(false);
  protected readonly draft = signal('');

  @ViewChild('titleInput') private set titleInput(input: ElementRef<HTMLInputElement> | undefined) {
    // The field takes the title's place with the title selected, ready to type over.
    input?.nativeElement.focus();
    input?.nativeElement.select();
  }

  /** A click on the title makes it a field (§11.6.1; the prototype's title button). */
  protected startRename(): void {
    if (!this.renamable()) return;
    this.draft.set((this.item() as { title?: string } | null)?.title ?? '');
    this.editing.set(true);
  }

  /** Escape: the title stays as it was. */
  protected cancelRename(): void {
    this.editing.set(false);
  }

  /**
   * Enter, or leaving the field. As the prototype's `saveTitle`: the text is trimmed, and an empty or
   * unchanged title is not saved; then "Title updated". The rename is the item menu's own ("Rename
   * Workbook"): the workbook is read fresh and only its title changed, so a pin or a flag set since it was
   * opened is not undone. The editor's engine takes the new title as its name through the app state (AM-48).
   */
  protected commitRename(): void {
    // Enter ends the edit, and the field leaving the page may then blur it: that second call does nothing.
    if (!this.editing()) return;
    this.editing.set(false);
    const item = this.item() as Sheet | null;
    // Cut by code points, as the server counts them, so an emoji at the 200th character is never split in half.
    const title = Array.from(this.draft().trim()).slice(0, TITLE_MAX).join('').trim();
    const service = this.sheetsService;
    if (!item?.id || !title || title === item.title || !service) return;
    this.pending.set(title);
    service
      .getItem(item.id)
      .pipe(
        take(1),
        switchMap((fresh) => {
          fresh.title = title;
          return service.update(fresh, { emit: true, displayToast: true, toastMessage: SHEETS_STRINGS['Title updated'] });
        }),
      )
      .subscribe({
        next: (saved) => {
          this.pending.set(null);
          // SBUX-1043: refresh the SECTION's copy only when this header heads the workbook the section
          // has open. The fourth panel mounts this header over a workbook of its own, which the main
          // app may not be looking at — setting the main view from there would drag the user's page
          // onto a workbook they renamed in a side panel. There the new name is kept here instead,
          // since nothing else will bring it back.
          if (this.selected()?.id === saved.id) this.stateService.setMainView(saved);
          else this.renamed.set({ id: saved.id, title: saved.title, at: saved.updatedAt });
        },
        // The service's toast says why; the stored title shows again.
        error: () => this.pending.set(null),
      });
  }

  protected typed(event: Event): void {
    this.draft.set((event.target as HTMLInputElement).value);
  }

  // ------------------------------------------------- the title bar's menus

  /** The open workbook for its item menu ("…", the prototype's "Item actions"); none for an unsaved item. */
  protected readonly menuItem = computed(() => {
    const item = this.item() as (SheetSummaryDto & { id?: string }) | null;
    return item?.id ? item : null;
  });

  private readonly downloads = inject(EditorDownloads);
  protected readonly downloadOpen = signal(false);
  /** Placed fixed under its button, as the editor placed it under the toolbar's. */
  protected readonly downloadAt = signal({ right: 8, top: 0 });

  protected toggleDownload(button: HTMLElement): void {
    if (this.downloadOpen()) {
      this.downloadOpen.set(false);
      return;
    }
    const box = button.getBoundingClientRect();
    this.downloadAt.set({ right: Math.max(8, window.innerWidth - box.right), top: box.bottom + 4 });
    this.downloadOpen.set(true);
  }

  /** What is picked runs in the editor below (`EditorDownloads`). */
  protected pickDownload(action: DownloadAction): void {
    this.downloadOpen.set(false);
    this.downloads.pick(action);
  }

  /** Up and Down move between the menu's items; Escape closes it, focus going back to its button. */
  protected downloadKey(event: KeyboardEvent, button: HTMLElement): void {
    if (!this.downloadOpen()) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.downloadOpen.set(false);
      button.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from((event.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('[role="menuitem"]'));
    const at = items.indexOf(document.activeElement as HTMLElement);
    event.preventDefault();
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at <= 0 ? items.length : at) - 1;
    items[next]?.focus();
  }

  /** A press anywhere but the menu and its button closes it (heard in the capture phase, as the editor's). */
  private readonly closeOutside = (event: Event): void => {
    if (this.downloadOpen() && !(event.target as HTMLElement | null)?.closest('.menuhost')) this.downloadOpen.set(false);
  };

  /** The item menu ("…"), open or not, as its popover reports it. */
  protected readonly itemMenuOpen = signal(false);
  @ViewChild('downloadButton') private readonly downloadButton?: ElementRef<HTMLElement>;
  @ViewChild('itemMenuButton') private readonly itemMenuButton?: ElementRef<HTMLElement>;

  public constructor() {
    document.addEventListener('pointerdown', this.closeOutside, true);
    // The MCP contract's sheets_ui (6 Oct) opens and closes these menus through the editor, as their buttons do.
    const detach = inject(EditorChrome).attach({
      downloadOpen: () => this.downloadOpen(),
      setDownloadOpen: (open) => {
        const button = this.downloadButton?.nativeElement;
        if (open !== this.downloadOpen() && button) this.toggleDownload(button);
      },
      itemMenuOpen: () => this.itemMenuOpen(),
      // The popover opens and closes on its button's click.
      setItemMenuOpen: (open) => {
        if (open !== this.itemMenuOpen()) this.itemMenuButton?.nativeElement.click();
      },
    });
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('pointerdown', this.closeOutside, true);
      detach();
    });
  }
}
